import { Dialog, IconButton, Slide, Box, AppBar, Toolbar, Typography, ImageList, ImageListItem, CircularProgress } from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import CloseIcon from '@mui/icons-material/Close';
import React, { useRef, useState } from 'react';
import { useHomeAttachmentsContext } from '../../contexts/HomeAttachmentsContext';
import ImageViewer from '../ImageViewer';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

interface AllImagesModalMobileProps {
  open: boolean;
  onClose: () => void;
}

const AllImagesModalMobile = ({ open, onClose }: AllImagesModalMobileProps) => {
  const [swipeOffset, setSwipeOffset] = useState(0);
  const touchStartY = useRef(0);
  const appBarRef = useRef<HTMLDivElement>(null);
  
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartY.current = e.touches[0].clientY;
  };
  
  const handleTouchMove = (e: React.TouchEvent) => {
    const currentY = e.touches[0].clientY;
    const diff = currentY - touchStartY.current;
    
    // Only allow downward swipes
    if (diff > 0) {
      setSwipeOffset(diff);
      // Prevent scrolling while swiping down
      e.preventDefault();
    }
  };
  
  const handleTouchEnd = () => {
    // If swiped down more than 100px, close the dialog
    if (swipeOffset > 100) {
      onClose();
    }
    // Reset the offset
    setSwipeOffset(0);
  };

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
      sx={{ zIndex: 2100 }}
    >
      <Box sx={{ 
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        transform: `translateY(${swipeOffset}px)`,
        transition: swipeOffset ? 'none' : 'transform 0.3s ease',
        opacity: Math.max(1 - swipeOffset / 200, 0.5)
      }}>
        <AppBar 
          position="static" 
          color="primary" 
          elevation={1} 
          sx={{ mb: 0 }}
          ref={appBarRef}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <Toolbar sx={{ minHeight: '56px' }}>
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              All Images
            </Typography>
            <IconButton
              edge="end"
              color="inherit"
              onClick={onClose}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>
        
        <Box sx={{ flex: 1, overflow: 'auto', p: 0 }}>
          {/* Use the existing AllImagesModal content */}
          <AllImagesContent />
        </Box>
      </Box>
    </Dialog>
  );
};

// Extracted content component to avoid nested modal issues
const AllImagesContent = () => {
  const { 
    attachments, 
    isLoading, 
    error,
    getAttachmentUrl,
    isImageFile,
    deleteAttachment
  } = useHomeAttachmentsContext();
  
  const [viewerOpen, setViewerOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  
  // Filter for only image attachments
  const imageAttachments = attachments.filter(att => isImageFile(att.fileType));

  // Get image URLs for display in the grid
  const imageUrls = imageAttachments.map(att => getAttachmentUrl(att));

  // Calculate columns based on screen size
  const getCols = () => {
    if (isMobile) return 2;
    if (isTablet) return 3;
    return 4;
  };
  
  const handleImageClick = (index: number) => {
    setSelectedImageIndex(index);
    setViewerOpen(true);
  };
  
  return (
    <Box sx={{ p: 2 }}>
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <Typography color="error">{error}</Typography>
        </Box>
      ) : imageUrls.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <Typography>No images available</Typography>
        </Box>
      ) : (
        <ImageList cols={getCols()} gap={16}>
          {imageUrls.map((url, index) => (
            <ImageListItem key={index} onClick={() => handleImageClick(index)}>
              <img
                src={url}
                alt={`Home image ${index + 1}`}
                loading="lazy"
                style={{
                  width: '100%',
                  aspectRatio: '4/3',
                  objectFit: 'cover',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              />
            </ImageListItem>
          ))}
        </ImageList>
      )}
      
      {/* Image Viewer for full-size display */}
      <ImageViewer
        open={viewerOpen}
        onClose={() => setViewerOpen(false)}
        attachments={imageAttachments}
        initialIndex={selectedImageIndex}
        onDelete={async (attachment) => {
          const remaining = imageAttachments.length - 1;
          const success = await deleteAttachment(attachment.key);
          return { success, remainingCount: remaining };
        }}
      />
    </Box>
  );
};

export default AllImagesModalMobile;